"use client";

import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Calendar, DollarSign, User, Send, FileText, Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export function CreateInvoiceModal({ 
  children,
  onInvoiceCreated
}: { 
  children: React.ReactNode;
  onInvoiceCreated?: (newInvoice: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [dueDate, setDueDate] = useState("2026-10-14");
  const [taxPercent, setTaxPercent] = useState<number>(7.5);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [items, setItems] = useState<LineItem[]>([
    { id: "1", description: "Software Development Services", quantity: 1, unitPrice: 250000 }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addItem = () => {
    setItems([
      ...items,
      { id: Date.now().toString(), description: "", quantity: 1, unitPrice: 0 }
    ]);
  };

  const updateItem = (id: string, field: keyof LineItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const taxAmount = (subtotal * (taxPercent || 0)) / 100;
  const total = Math.max(0, subtotal + taxAmount - (discountAmount || 0));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      const newInvoice = {
        id: `INV-2026-${Math.floor(1000 + Math.random() * 9000)}`,
        customer: customerName || "Acme Corporation",
        email: customerEmail || "billing@acme.com",
        amount: `₦${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        subtotal: subtotal,
        tax: taxAmount,
        dueDate: dueDate,
        createdDate: "Today",
        status: "Pending",
        type: "warning",
        items: items
      };

      onInvoiceCreated?.(newInvoice);
      setIsSubmitting(false);
      setOpen(false);
    }, 1000);
  };

  return (
    <>
      <div onClick={() => setOpen(true)} className="inline-block">
        {children}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <div className="flex items-center gap-3 border-b pb-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold">Create New Invoice</DialogTitle>
                <DialogDescription className="text-xs">
                  Issue a payment invoice with auto-generated payment link and auto-reconciled NUBAN.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 pt-2">
            
            {/* Customer & Invoice Meta Details */}
            <div className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">1. Customer & Invoice Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-xl bg-muted/20 border">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-bold text-foreground">Customer Name / Company</Label>
                  <Input 
                    required
                    placeholder="e.g. Acme Corporation Ltd" 
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="h-9 text-xs" 
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-bold text-foreground">Customer Email Address</Label>
                  <Input 
                    required
                    type="email"
                    placeholder="billing@acme.com" 
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="h-9 text-xs" 
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-bold text-foreground">Payment Due Date</Label>
                  <Input 
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="h-9 text-xs font-mono" 
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-bold text-foreground">Currency</Label>
                  <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs font-bold">
                    <option value="NGN">NGN (Nigerian Naira ₦)</option>
                    <option value="USD">USD (US Dollar $)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Line Items Builder */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">2. Line Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-7 text-xs font-bold border-primary/30 text-primary hover:bg-primary/10">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Line Item
                </Button>
              </div>

              {/* Table / Grid Headers for Desktop */}
              <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 text-[11px] font-bold text-muted-foreground bg-muted/40 rounded-lg border">
                <div className="col-span-5">ITEM DESCRIPTION</div>
                <div className="col-span-2 text-center">QTY</div>
                <div className="col-span-2 text-right">UNIT PRICE (₦)</div>
                <div className="col-span-2 text-right">SUBTOTAL (₦)</div>
                <div className="col-span-1 text-center">ACTION</div>
              </div>

              <div className="space-y-2">
                {items.map((item) => {
                  const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
                  return (
                    <div key={item.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-center bg-card p-3 sm:p-2 rounded-xl border shadow-xs">
                      <div className="sm:col-span-5 space-y-1">
                        <span className="sm:hidden text-[10px] font-bold text-muted-foreground">Description</span>
                        <Input 
                          placeholder="Item or service description"
                          value={item.description}
                          onChange={(e) => updateItem(item.id, "description", e.target.value)}
                          className="h-9 sm:h-8 text-xs"
                        />
                      </div>

                      <div className="grid grid-cols-3 sm:contents gap-2">
                        <div className="sm:col-span-2 space-y-1">
                          <span className="sm:hidden text-[10px] font-bold text-muted-foreground">Qty</span>
                          <Input 
                            type="number"
                            min={1}
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, "quantity", Number(e.target.value))}
                            className="h-9 sm:h-8 text-xs font-mono text-center"
                          />
                        </div>
                        <div className="sm:col-span-2 space-y-1">
                          <span className="sm:hidden text-[10px] font-bold text-muted-foreground">Unit Price</span>
                          <Input 
                            type="number"
                            placeholder="Price"
                            value={item.unitPrice || ""}
                            onChange={(e) => updateItem(item.id, "unitPrice", Number(e.target.value))}
                            className="h-9 sm:h-8 text-xs font-mono text-right"
                          />
                        </div>
                        <div className="sm:col-span-2 space-y-1 flex flex-col justify-center sm:block">
                          <span className="sm:hidden text-[10px] font-bold text-muted-foreground">Subtotal</span>
                          <div className="h-9 sm:h-8 flex items-center justify-end px-2 bg-muted/20 rounded-md font-mono text-xs font-bold text-foreground">
                            ₦{lineTotal.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-1 flex justify-end sm:justify-center pt-2 sm:pt-0 border-t sm:border-t-0">
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          onClick={() => removeItem(item.id)}
                          disabled={items.length === 1}
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          title="Remove Item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Calculations & Summary Side-by-Side on Desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-muted/20 border space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Tax & Discounts</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">VAT / Tax Rate (%)</Label>
                    <Input 
                      type="number"
                      value={taxPercent}
                      onChange={(e) => setTaxPercent(Number(e.target.value))}
                      className="h-9 text-xs font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Discount (₦)</Label>
                    <Input 
                      type="number"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(Number(e.target.value))}
                      className="h-9 text-xs font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-2.5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-primary">Financial Summary</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span className="font-mono font-semibold text-foreground">₦{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>VAT ({taxPercent}%)</span>
                    <span className="font-mono font-semibold text-foreground">+₦{taxAmount.toLocaleString()}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Discount</span>
                      <span className="font-mono font-bold">-₦{discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-bold text-foreground pt-2 border-t border-primary/20">
                    <span>Total Amount Due</span>
                    <span className="font-mono text-primary text-lg">₦{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-3 border-t flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)} className="h-9 text-xs font-semibold">
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSubmitting} className="h-9 px-6 bg-primary text-primary-foreground font-bold text-xs shadow-sm">
                {isSubmitting ? "Generating Invoice..." : "Generate & Send Invoice"}
              </Button>
            </DialogFooter>

          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
